import React from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { UserResource, UserAdminInfo } from "../../api/users";
import { GroupUserRole } from "../../api/groups";
import { useGroupsStore } from "../../store/groupsStore";

interface AddMemberDialogProps {
  open: boolean;
  groupId: string;
  onClose: () => void;
}

const ROLE_OPTIONS: { value: GroupUserRole; label: string }[] = [
  { value: "member", label: "Member" },
  { value: "admin", label: "Admin" },
  { value: "owner", label: "Owner" },
];

export const AddMemberDialog: React.FC<AddMemberDialogProps> = ({
  open,
  groupId,
  onClose,
}) => {
  const { addMember } = useGroupsStore();

  const [email, setEmail] = React.useState("");
  const [role, setRole] = React.useState<GroupUserRole>("member");
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [matchedUser, setMatchedUser] = React.useState<UserAdminInfo | null>(null);
  const [searchError, setSearchError] = React.useState<string>();
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string>();

  const handleClose = () => {
    setEmail("");
    setRole("member");
    setMatchedUser(null);
    setSearchError(undefined);
    setSubmitError(undefined);
    onClose();
  };

  const handleSearch = async () => {
    if (!email.trim()) return;
    setSearchLoading(true);
    setMatchedUser(null);
    setSearchError(undefined);
    try {
      const result = await UserResource.getList({ filter: email.trim() });
      const exact = result.users.find(
        (u) => u.email.toLowerCase() === email.trim().toLowerCase()
      );
      const match = exact ?? result.users[0];
      if (!match) {
        setSearchError("No user found with that email.");
      } else if (!match.fsId) {
        setSearchError(
          `User "${match.email}" was found but has no app user ID (fsId). They may not have completed sign-up.`
        );
      } else {
        setMatchedUser(match);
      }
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : "Search failed.");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!matchedUser?.fsId) return;
    setSubmitLoading(true);
    setSubmitError(undefined);
    try {
      await addMember(groupId, matchedUser.fsId, role);
      handleClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to add member.");
      setSubmitLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add Member</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <TextField
              fullWidth
              label="User email"
              size="small"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setMatchedUser(null);
                setSearchError(undefined);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
            <Button
              variant="outlined"
              onClick={handleSearch}
              disabled={searchLoading || !email.trim()}
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
              {searchLoading ? <CircularProgress size={18} /> : "Search"}
            </Button>
          </Box>

          {searchError && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              {searchError}
            </Alert>
          )}

          {matchedUser && (
            <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                {matchedUser.displayName || matchedUser.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {matchedUser.email}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                App ID: {matchedUser.fsId}
              </Typography>
            </Box>
          )}

          <TextField
            select
            fullWidth
            label="Role"
            size="small"
            value={role}
            onChange={(e) => setRole(e.target.value as GroupUserRole)}
          >
            {ROLE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          {submitError && (
            <Alert severity="error" sx={{ py: 0.5 }}>
              {submitError}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!matchedUser || submitLoading}
        >
          {submitLoading ? "Adding..." : "Add to Group"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
